import classNames from "classnames";
import React from "react";
import { actions, Control } from "react-redux-form";
import _ from "underscore";

import { Errors, NumberStepper } from "../../../../core/atoms/rrf";
import { CommonProps, EnquiryFormSection, getTravellerCount } from "./common";
import { FormProgressIcon } from "./formProgressIcon";

import "./ageRanges.scss";

interface OwnProps {
  dispatch: any;
  ageRanges: IAgeRange[];
}

interface ISortedAgeRanges {
  children: IAgeRange[];
  adults: IAgeRange[];
}

const DEFAULT_TRAVELLERS: Forms.EnquiryFormTravellers = {
  childrenIncluded: false,
  ageRangeIds: {},
  roomPreferences: "",
};

const RoomPrefsThreshold = 2;
const roomPrefsRequired = (val) => val == null || val.trim().length < 1;

export class AgeRanges extends EnquiryFormSection<OwnProps> {
  private sortedAgeRanges: ISortedAgeRanges;

  public constructor(props) {
    super(props);
    this.sortedAgeRanges = this.sortAgeRanges();
  }

  private maxTravellerCount = 100;

  // Sorts age ranges by spearating adults and childre, and putting then putting
  // them in ascending order.
  private sortAgeRanges = (): ISortedAgeRanges => {
    const adultMinAge: number = 18;

    let adults: IAgeRange[] = [];
    let children: IAgeRange[] = [];

    this.props.ageRanges.forEach(ageRange => {
      if (ageRange.min >= adultMinAge) {
        adults.push(ageRange);
      }
      else {
        children.push(ageRange);
      }
    });

    adults = _.sortBy(adults, "min");
    children = _.sortBy(children, "min");

    return { adults, children };
  }

  private includeChildren = (travellers: Forms.EnquiryFormTravellers) => {
    const childIds = _.pluck(this.sortedAgeRanges.children, "id").map(x => x.toString());
    const withoutChildren = _.omit(travellers.ageRangeIds, ...childIds);

    const resetActions = [
      actions.change(`${this.props.model}.ageRangeIds`, withoutChildren),
      actions.change(`${this.props.model}.childrenIncluded`, true),
    ];

    if (withoutChildren.length <= RoomPrefsThreshold) {
      resetActions.push(actions.change(`${this.props.model}.roomPreferences`, ""));
    }

    this.props.dispatch(actions.batch(this.props.model, resetActions));
  }

  private renderSpinner = (ageRange: IAgeRange) => {
    const ageRangeModel = `${this.props.model}.ageRangeIds.${ageRange.id}`;
    const label = `${ageRange.name} yrs`;

    return (
      <div className="age-ranges__stepper" key={ageRange.id}>
        <label htmlFor={ageRangeModel}>{label}</label>
        <NumberStepper
          id={ageRangeModel}
          title={label}
          model={ageRangeModel}
          min={0}
          max={99}
        />
      </div>
    )
  }

  protected shouldUpdateValidity(oldProps: CommonProps, newProps: CommonProps) {
    return !_.isEqual(oldProps.formState.travellers, newProps.formState.travellers);
  }

  protected syncValidity(formState: Forms.EnquiryForm) {
    const state = formState.travellers;
    if (state == null) {
      return;
    }

    const travellerCount = getTravellerCount(state);
    const errors = {
      agesRequired: travellerCount < 1,
      roomPrefsRequired: travellerCount > RoomPrefsThreshold && roomPrefsRequired(state.roomPreferences),
      travellerCountMax: travellerCount > this.maxTravellerCount,
    };

    this.props.dispatch(actions.setErrors(this.props.model, errors));
  }

  public render() {
    const { model, className, id, formState } = this.props;
    const travellers = formState.travellers || DEFAULT_TRAVELLERS;
    const travellerCount = getTravellerCount(travellers);

    return (
      <section id={id} className={classNames("age-ranges", className)}>
        <header>
          <FormProgressIcon model={model} />
          <h2>Who's travelling?</h2>
          <p>How many people are in your party? Providing your ages helps us tailor-make your trip.</p>
        </header>
        <div className="enquiry-form-question">
          <Errors model={model} messages={{
            agesRequired: "Please tell us who will be travelling",
            travellerCountMax: `The maximum number of people who can travel is ${this.maxTravellerCount}`,
          }} />

          <div className="adults stepper-wrap">
            <header>
              <h3>Adults</h3>
            </header>
            <div className="steppers">
              {this.sortedAgeRanges.adults.map(ageRange => this.renderSpinner(ageRange))}
            </div>
          </div>

          {!travellers.childrenIncluded &&
            <div className="include-children-wrap">
              <a onClick={() => this.includeChildren(travellers)}>Add children...</a>
            </div>
          }

          {travellers.childrenIncluded &&
            <div className="children stepper-wrap">
              <header>
                <h3>Children</h3>
              </header>

              <div className="steppers">
                {this.sortedAgeRanges.children.map(ageRange => this.renderSpinner(ageRange))}
              </div>
            </div>
          }

          {travellerCount > RoomPrefsThreshold &&
            <div className="room-preferences">
              <h2>What are your room preferences?</h2>
              <Errors model={`${model}.roomPreferences`} messages={{
                roomPrefsRequired: "Please tell us your preferences for sleeping arrangements",
              }} />
              <p>Since there's more than two of you, we'd like to know your preferences for sleeping arrangements.</p>
              <Control.textarea
                model={`${model}.roomPreferences`}
                placeholder="Enter your preferences here (required)"
                aria-label="Enter your preferences for sleeping arrangements"
              />
            </div>
          }
        </div>
      </section>
    );
  }
}
